import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface Payment {
  id: string;
  type: 'subscription' | 'workwear' | 'job_invoice' | 'tip' | 'deposit' | 'van_rental';
  amount: number;
  status: string;
  created_at: string;
  customer_email?: string;
  customer_name?: string;
  provider_name?: string;
  service_name?: string;
  payment_method?: string;
  stripe_payment_id?: string;
  metadata?: any;
}

interface PaymentsManagementProps {
  admin?: any;
}

export default function PaymentsManagement({ admin }: PaymentsManagementProps = {}) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'services' | 'methods' | 'timeline'>('overview');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [isProcessingRefund, setIsProcessingRefund] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [services, setServices] = useState<any[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    loadAllPayments();
    loadServices();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [typeFilter, statusFilter, dateRange, searchTerm, payments]);

  const loadServices = async () => {
    const { data } = await supabase
      .from('services')
      .select('*')
      .eq('is_active', true);
    if (data) setServices(data);
  };

  const loadAllPayments = async () => {
    try {
      // Load job invoices
      const { data: invoices } = await supabase
        .from('invoices')
        .select('*')
        .order('created_at', { ascending: false });

      // Load jobs for service info
      const { data: jobs } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false });

      // Transform invoices to payment format
      const invoicePayments: Payment[] = (invoices || []).map(inv => {
        const relatedJob = jobs?.find(j => j.id === inv.job_id);
        return {
          id: inv.id,
          type: 'job_invoice' as const,
          amount: parseFloat(inv.total_amount || 0),
          status: inv.status,
          created_at: inv.created_at,
          customer_name: relatedJob?.customer_name,
          customer_email: relatedJob?.customer_email,
          service_name: relatedJob?.service_type,
          payment_method: inv.payment_method || 'card',
          stripe_payment_id: inv.stripe_payment_id,
          metadata: { ...inv, job: relatedJob }
        };
      });

      // Load tips from jobs
      const tipPayments: Payment[] = (jobs || [])
        .filter(job => job.tip_amount && parseFloat(job.tip_amount) > 0)
        .map(job => ({
          id: `tip-${job.id}`,
          type: 'tip' as const,
          amount: parseFloat(job.tip_amount || 0),
          status: 'paid',
          created_at: job.created_at,
          customer_name: job.customer_name,
          service_name: job.service_type,
          payment_method: 'card',
          metadata: job
        }));

      // Load subscription payments
      const { data: subscriptions } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      const subscriptionPayments: Payment[] = (subscriptions || []).map(sub => ({
        id: `sub-${sub.id}`,
        type: 'subscription' as const,
        amount: parseFloat(sub.amount || 29.99),
        status: sub.status === 'active' ? 'paid' : sub.status,
        created_at: sub.created_at,
        provider_name: sub.provider_name,
        payment_method: 'card',
        metadata: sub
      }));

      // Load workwear orders
      const { data: workwearOrders } = await supabase
        .from('workwear_orders')
        .select('*')
        .order('created_at', { ascending: false });

      const workwearPayments: Payment[] = (workwearOrders || []).map(order => ({
        id: `workwear-${order.id}`,
        type: 'workwear' as const,
        amount: parseFloat(order.total_amount || 0),
        status: order.payment_status || 'pending',
        created_at: order.created_at,
        provider_name: order.provider_name,
        payment_method: 'card',
        metadata: order
      }));

      const allPayments = [...invoicePayments, ...tipPayments, ...subscriptionPayments, ...workwearPayments];
      setPayments(allPayments);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = [...payments];

    if (typeFilter !== 'all') {
      filtered = filtered.filter(p => p.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.customer_name?.toLowerCase().includes(term) ||
        p.customer_email?.toLowerCase().includes(term) ||
        p.provider_name?.toLowerCase().includes(term) ||
        p.service_name?.toLowerCase().includes(term) ||
        p.id.toLowerCase().includes(term)
      );
    }

    if (dateRange !== 'all') {
      const now = new Date();
      let startDate: Date;
      switch (dateRange) {
        case 'today':
          startDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case 'quarter':
          startDate = new Date(now.setMonth(now.getMonth() - 3));
          break;
        default:
          startDate = new Date(0);
      }
      filtered = filtered.filter(p => new Date(p.created_at) >= startDate);
    }

    setFilteredPayments(filtered);
  };

  const handleRefundClick = (payment: any) => {
    setSelectedPayment(payment);
    setRefundAmount(payment.amount.toString());
    setRefundReason('requested_by_customer');
    setShowRefundModal(true);
    setRefundSuccess(false);
  };

  const processRefund = async () => {
    if (!selectedPayment || !refundAmount) return;

    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0 || amount > selectedPayment.amount) {
      alert('Invalid refund amount');
      return;
    }

    try {
      setIsProcessingRefund(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/process-refund`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentIntentId: selectedPayment.stripe_payment_id,
            amount: amount,
            reason: refundReason,
            type: selectedPayment.type,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to process refund');
      }

      setRefundSuccess(true);
      setTimeout(() => {
        setShowRefundModal(false);
        loadAllPayments();
      }, 2000);
    } catch (error) {
      console.error('Error processing refund:', error);
      alert(error instanceof Error ? error.message : 'Failed to process refund');
    } finally {
      setIsProcessingRefund(false);
    }
  };

  const viewPaymentDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowDetails(true);
  };

  const togglePaymentSelection = (paymentId: string) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId) 
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const selectAllPayments = () => {
    if (selectedPayments.length === filteredPayments.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(filteredPayments.map(p => p.id));
    }
  };

  const exportPayments = (format: 'csv' | 'json') => {
    const dataToExport = selectedPayments.length > 0 
      ? filteredPayments.filter(p => selectedPayments.includes(p.id))
      : filteredPayments;

    if (format === 'csv') {
      const headers = ['ID', 'Type', 'Amount', 'Status', 'Date', 'Customer', 'Service', 'Payment Method'];
      const rows = dataToExport.map(p => [
        p.id,
        p.type,
        p.amount.toFixed(2),
        p.status,
        new Date(p.created_at).toLocaleDateString(),
        p.customer_name || p.provider_name || 'N/A',
        p.service_name || 'N/A',
        p.payment_method || 'N/A'
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      downloadFile(csv, 'payments-export.csv', 'text/csv');
    } else {
      const json = JSON.stringify(dataToExport, null, 2);
      downloadFile(json, 'payments-export.json', 'application/json');
    }
    setShowExportModal(false);
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate stats
  const stats = {
    totalRevenue: payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0),
    pendingAmount: payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0),
    subscriptionRevenue: payments.filter(p => p.type === 'subscription' && p.status === 'paid').reduce((sum, p) => sum + p.amount, 0),
    jobRevenue: payments.filter(p => p.type === 'job_invoice' && p.status === 'paid').reduce((sum, p) => sum + p.amount, 0),
    tipRevenue: payments.filter(p => p.type === 'tip').reduce((sum, p) => sum + p.amount, 0),
    workwearRevenue: payments.filter(p => p.type === 'workwear' && p.status === 'paid').reduce((sum, p) => sum + p.amount, 0),
    totalTransactions: payments.length,
    successRate: payments.length > 0 ? (payments.filter(p => p.status === 'paid').length / payments.length * 100).toFixed(1) : '0'
  };

  // Service revenue breakdown
  const serviceRevenue = services.map(service => {
    const servicePayments = payments.filter(p => 
      p.service_name?.toLowerCase().includes(service.name.toLowerCase()) && p.status === 'paid'
    );
    return {
      ...service,
      revenue: servicePayments.reduce((sum, p) => sum + p.amount, 0),
      transactions: servicePayments.length
    };
  }).sort((a, b) => b.revenue - a.revenue);

  // Payment method breakdown
  const paymentMethods = {
    card: payments.filter(p => p.payment_method === 'card').length,
  };

  // Timeline data (last 7 days)
  const getTimelineData = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayPayments = payments.filter(p => p.created_at.startsWith(dateStr));
      days.push({
        date: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' }),
        revenue: dayPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0),
        transactions: dayPayments.length
      });
    }
    return days;
  };

  const timelineData = getTimelineData();
  const maxRevenue = Math.max(...timelineData.map(d => d.revenue), 1);

  const getPaymentTypeLabel = (type: string) => {
    switch (type) {
      case 'subscription': return 'Subscription';
      case 'workwear': return 'Workwear';
      case 'job_invoice': return 'Job Invoice';
      case 'tip': return 'Tip';
      case 'deposit': return 'Deposit';
      case 'van_rental': return 'Van Rental';
      default: return type;
    }
  };

  const getPaymentTypeBadge = (type: string) => {
    switch (type) {
      case 'subscription': return 'bg-purple-100 text-purple-800';
      case 'workwear': return 'bg-blue-100 text-blue-800';
      case 'job_invoice': return 'bg-teal-100 text-teal-800';
      case 'tip': return 'bg-amber-100 text-amber-800';
      case 'deposit': return 'bg-indigo-100 text-indigo-800';
      case 'van_rental': return 'bg-cyan-100 text-cyan-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payments Management</h2>
          <p className="text-gray-600 mt-1">Advanced payment tracking, analytics, and service revenue management</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowExportModal(true)}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-line"></i>
            Export
          </button>
          <button
            onClick={loadAllPayments}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Total Revenue</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.totalRevenue.toFixed(2)}</p>
              <p className="text-xs text-white/70 mt-1">{stats.totalTransactions} transactions</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-money-pound-circle-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Pending Payments</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.pendingAmount.toFixed(2)}</p>
              <p className="text-xs text-white/70 mt-1">Awaiting confirmation</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Job Payments</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.jobRevenue.toFixed(2)}</p>
              <p className="text-xs text-white/70 mt-1">From completed jobs</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-briefcase-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Success Rate</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.successRate}%</p>
              <p className="text-xs text-white/70 mt-1">Payment completion</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-2xl text-white"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
              { id: 'transactions', label: 'Transactions', icon: 'ri-exchange-line' },
              { id: 'services', label: 'Service Revenue', icon: 'ri-service-line' },
              { id: 'methods', label: 'Payment Methods', icon: 'ri-bank-card-line' },
              { id: 'timeline', label: 'Timeline', icon: 'ri-line-chart-line' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} w-5 h-5 flex items-center justify-center`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Revenue Breakdown */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="ri-vip-crown-line text-purple-600"></i>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Subscriptions</p>
                    <p className="text-2xl font-bold text-purple-600">£{stats.subscriptionRevenue.toFixed(2)}</p>
                  </div>
                  <div className="text-center p-4 bg-teal-50 rounded-lg border border-teal-100">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="ri-file-list-3-line text-teal-600"></i>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Job Invoices</p>
                    <p className="text-2xl font-bold text-teal-600">£{stats.jobRevenue.toFixed(2)}</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="ri-t-shirt-line text-blue-600"></i>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Workwear</p>
                    <p className="text-2xl font-bold text-blue-600">£{stats.workwearRevenue.toFixed(2)}</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="ri-gift-line text-amber-600"></i>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Tips</p>
                    <p className="text-2xl font-bold text-amber-600">£{stats.tipRevenue.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Payment Status Distribution</h4>
                  <div className="space-y-3">
                    {['paid', 'pending', 'failed', 'refunded'].map(status => {
                      const count = payments.filter(p => p.status === status).length;
                      const percentage = payments.length > 0 ? (count / payments.length * 100).toFixed(1) : '0';
                      return (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${
                              status === 'paid' ? 'bg-green-500' :
                              status === 'pending' ? 'bg-yellow-500' :
                              status === 'failed' ? 'bg-red-500' : 'bg-gray-500'
                            }`}></span>
                            <span className="text-sm text-gray-700 capitalize">{status}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{count}</span>
                            <span className="text-xs text-gray-500">({percentage}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Payment Type Distribution</h4>
                  <div className="space-y-3">
                    {['job_invoice', 'subscription', 'tip', 'workwear'].map(type => {
                      const count = payments.filter(p => p.type === type).length;
                      const revenue = payments.filter(p => p.type === type && p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
                      return (
                        <div key={type} className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentTypeBadge(type)}`}>
                            {getPaymentTypeLabel(type)}
                          </span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-900">£{revenue.toFixed(2)}</span>
                            <span className="text-xs text-gray-500 ml-1">({count})</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Recent Activity</h4>
                  <div className="space-y-3">
                    {payments.slice(0, 5).map(payment => (
                      <div key={payment.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            payment.status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'
                          }`}></span>
                          <span className="text-sm text-gray-700 truncate max-w-[120px]">
                            {payment.customer_name || payment.provider_name || 'Unknown'}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">£{payment.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search by name, email, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="all">All Types</option>
                  <option value="job_invoice">Job Invoices</option>
                  <option value="subscription">Subscriptions</option>
                  <option value="tip">Tips</option>
                  <option value="workwear">Workwear</option>
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="all">All Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last 90 Days</option>
                </select>
              </div>

              {/* Bulk Actions */}
              {selectedPayments.length > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-sm text-teal-800">
                    <strong>{selectedPayments.length}</strong> payments selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowExportModal(true)}
                      className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm cursor-pointer whitespace-nowrap"
                    >
                      Export Selected
                    </button>
                    <button
                      onClick={() => setSelectedPayments([])}
                      className="px-3 py-1.5 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm cursor-pointer whitespace-nowrap"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}

              {/* Transactions Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedPayments.length === filteredPayments.length && filteredPayments.length > 0}
                            onChange={selectAllPayments}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer/Provider</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPayments.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                            <i className="ri-inbox-line text-4xl text-gray-300 mb-2 block"></i>
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        filteredPayments.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={selectedPayments.includes(payment.id)}
                                onChange={() => togglePaymentSelection(payment.id)}
                                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm font-medium text-gray-900">{payment.id.slice(0, 8)}...</div>
                              <div className="text-xs text-gray-500">{payment.payment_method || 'Card'}</div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getPaymentTypeBadge(payment.type)}`}>
                                {getPaymentTypeLabel(payment.type)}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm font-medium text-gray-900">
                                {payment.customer_name || payment.provider_name || 'N/A'}
                              </div>
                              {payment.customer_email && (
                                <div className="text-xs text-gray-500">{payment.customer_email}</div>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-700">
                              {payment.service_name || '-'}
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm font-bold text-gray-900">£{payment.amount.toFixed(2)}</div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadge(payment.status)}`}>
                                {payment.status}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-500">
                              {new Date(payment.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => viewPaymentDetails(payment)}
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
                                  title="View Details"
                                >
                                  <i className="ri-eye-line"></i>
                                </button>
                                {payment.status === 'paid' && (
                                  <button
                                    onClick={() => handleRefundClick(payment)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                                    title="Refund"
                                  >
                                    <i className="ri-refund-2-line"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination Info */}
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Showing {filteredPayments.length} of {payments.length} transactions</span>
              </div>
            </div>
          )}

          {/* Service Revenue Tab */}
          {activeTab === 'services' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Revenue by Service</h3>
                <span className="text-sm text-gray-500">{services.length} active services</span>
              </div>

              {serviceRevenue.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="ri-service-line text-4xl text-gray-300 mb-2 block"></i>
                  No service revenue data available
                </div>
              ) : (
                <div className="space-y-4">
                  {serviceRevenue.map((service, index) => (
                    <div key={service.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                            <i className={`${service.icon || 'ri-tools-line'} text-teal-600`}></i>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{service.name}</h4>
                            <p className="text-xs text-gray-500">{service.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">£{service.revenue.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{service.transactions} transactions</p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-teal-500 h-2 rounded-full transition-all"
                          style={{ width: `${serviceRevenue[0]?.revenue > 0 ? (service.revenue / serviceRevenue[0].revenue * 100) : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Payment Methods Tab */}
          {activeTab === 'methods' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                {/* Card Payments */}
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className="ri-bank-card-line text-2xl"></i>
                    </div>
                    <span className="text-3xl font-bold">{paymentMethods.card}</span>
                  </div>
                  <h4 className="font-semibold">Card Payments</h4>
                  <p className="text-sm text-white/80">Secure online card transactions</p>
                </div>
              </div>

              {/* Payment Distribution */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Payment Distribution</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700">Card Payments</span>
                      <span className="text-sm font-medium text-gray-900">100%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="h-3 rounded-full transition-all bg-indigo-500"
                        style={{ width: '100%' }}
                      ></div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">All payments are processed securely through Stripe</p>
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Timeline (Last 7 Days)</h3>

              {/* Chart */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-end justify-between h-48 gap-2">
                  {timelineData.map((day, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div className="w-full flex flex-col items-center justify-end h-40">
                        <div 
                          className="w-full bg-teal-500 rounded-t-lg transition-all hover:bg-teal-600 cursor-pointer relative group"
                          style={{ height: `${maxRevenue > 0 ? (day.revenue / maxRevenue * 100) : 0}%`, minHeight: day.revenue > 0 ? '8px' : '0' }}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            £{day.revenue.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 mt-2">{day.date}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Daily Summary */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Daily Breakdown</h4>
                  <div className="space-y-3">
                    {timelineData.map((day, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <span className="text-sm text-gray-700">{day.date}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-gray-500">{day.transactions} txns</span>
                          <span className="text-sm font-medium text-gray-900">£{day.revenue.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Period Summary</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Revenue</span>
                      <span className="text-xl font-bold text-gray-900">
                        £{timelineData.reduce((sum, d) => sum + d.revenue, 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Transactions</span>
                      <span className="text-xl font-bold text-gray-900">
                        {timelineData.reduce((sum, d) => sum + d.transactions, 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Daily Average</span>
                      <span className="text-xl font-bold text-gray-900">
                        £{(timelineData.reduce((sum, d) => sum + d.revenue, 0) / 7).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Best Day</span>
                      <span className="text-xl font-bold text-teal-600">
                        £{Math.max(...timelineData.map(d => d.revenue)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Export Payments</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              {selectedPayments.length > 0 
                ? `Export ${selectedPayments.length} selected payments`
                : `Export all ${filteredPayments.length} filtered payments`
              }
            </p>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => exportPayments('csv')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all cursor-pointer text-center"
              >
                <i className="ri-file-excel-line text-3xl text-green-600 mb-2 block"></i>
                <span className="font-medium text-gray-900">CSV Format</span>
                <p className="text-xs text-gray-500 mt-1">For Excel/Sheets</p>
              </button>
              <button
                onClick={() => exportPayments('json')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all cursor-pointer text-center"
              >
                <i className="ri-code-s-slash-line text-3xl text-blue-600 mb-2 block"></i>
                <span className="font-medium text-gray-900">JSON Format</span>
                <p className="text-xs text-gray-500 mt-1">For developers</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {refundSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Refund Processed!</h3>
                <p className="text-gray-600">
                  £{parseFloat(refundAmount).toFixed(2)} has been refunded successfully.
                </p>
              </div>
            ) : (
              <>
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-gray-900">Process Refund</h3>
                    <button
                      onClick={() => setShowRefundModal(false)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Payment Type:</span>
                      <span className="font-medium text-gray-900">{getPaymentTypeLabel(selectedPayment.type)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Original Amount:</span>
                      <span className="font-medium text-gray-900">£{selectedPayment.amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium text-gray-900">{selectedPayment.customer_name || selectedPayment.provider_name || 'N/A'}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Refund Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                      <input
                        type="number"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        max={selectedPayment.amount}
                        min="0.01"
                        step="0.01"
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setRefundAmount((selectedPayment.amount / 2).toFixed(2))}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        50%
                      </button>
                      <button
                        onClick={() => setRefundAmount(selectedPayment.amount.toFixed(2))}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        Full Amount
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Refund Reason</label>
                    <select
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                    >
                      <option value="requested_by_customer">Requested by Customer</option>
                      <option value="duplicate">Duplicate Payment</option>
                      <option value="fraudulent">Fraudulent</option>
                      <option value="service_not_provided">Service Not Provided</option>
                      <option value="quality_issue">Quality Issue</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-3">
                    <i className="ri-alert-line text-yellow-600 text-lg flex-shrink-0"></i>
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">This action cannot be undone</p>
                      <p>The refund will be processed immediately through Stripe.</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-200 flex gap-3">
                  <button
                    onClick={() => setShowRefundModal(false)}
                    disabled={isProcessingRefund}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={processRefund}
                    disabled={isProcessingRefund || !refundAmount}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    {isProcessingRefund ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ri-refund-2-line"></i>
                        Process Refund
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showDetails && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Payment Details</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${
                selectedPayment.status === 'paid' ? 'bg-green-50 border border-green-200' :
                selectedPayment.status === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedPayment.status === 'paid' ? 'bg-green-100' :
                    selectedPayment.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                  }`}>
                    <i className={`text-xl ${
                      selectedPayment.status === 'paid' ? 'ri-check-line text-green-600' :
                      selectedPayment.status === 'pending' ? 'ri-time-line text-yellow-600' :
                      'ri-close-line text-red-600'
                    }`}></i>
                  </div>
                  <div>
                    <p className={`font-semibold capitalize ${
                      selectedPayment.status === 'paid' ? 'text-green-800' :
                      selectedPayment.status === 'pending' ? 'text-yellow-800' : 'text-red-800'
                    }`}>
                      Payment {selectedPayment.status}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(selectedPayment.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="text-center py-4">
                <p className="text-sm text-gray-600 mb-1">Amount</p>
                <p className="text-4xl font-bold text-gray-900">£{selectedPayment.amount.toFixed(2)}</p>
              </div>

              {/* Details Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payment ID</p>
                  <p className="font-medium text-gray-900 text-sm break-all">{selectedPayment.id}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Type</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentTypeBadge(selectedPayment.type)}`}>
                    {getPaymentTypeLabel(selectedPayment.type)}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Customer/Provider</p>
                  <p className="font-medium text-gray-900">{selectedPayment.customer_name || selectedPayment.provider_name || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payment Method</p>
                  <p className="font-medium text-gray-900 capitalize">{selectedPayment.payment_method || 'Card'}</p>
                </div>
                {selectedPayment.service_name && (
                  <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Service</p>
                    <p className="font-medium text-gray-900">{selectedPayment.service_name}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {selectedPayment.status === 'paid' && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      handleRefundClick(selectedPayment);
                    }}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refund-2-line"></i>
                    Process Refund
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
